// src/components/studentPortal/StudentToolSelector.jsx
// New, additive, Student-Portal-only page - visually inspired by
// ProjectSelector.jsx's tool tiles, but never imports from or touches that
// file. Launches tools via the EXISTING free-tier routes (POST /generateScript
// etc., gated by begin_tool_run) by navigating straight to the same
// destination pages ProjectSelector.jsx's own "Launch Tool" button already
// uses (script.jsx / Budget.jsx / actorsCasting.jsx / Location.jsx) - none of
// which have any subscription/activateTool/verifyToolAccess check of their
// own. ProjectSelector.jsx, route_projectTool.py, verifyToolAccess,
// activateTool, and all subscription/payment code are untouched.
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiFileText, FiDollarSign, FiUsers, FiMapPin, FiArrowLeft, FiEye } from 'react-icons/fi';
import { baseUrl } from '../../config';

// Same 4 tools, same order/names as ProjectSelector.jsx's toolsData, same
// tool_key strings free_tier.py's TOOL_BIT_BY_KEY uses (script/budget/
// casting/location) so tools_used_this_week lookups match exactly.
const TOOLS = [
  { key: 'script', name: 'Script Analysis', desc: "Get AI insights on your script's pacing, character development, and marketability", icon: FiFileText, path: '/input' },
  { key: 'budget', name: 'Budgeting', desc: 'Intelligent budget forecasting based on similar successful productions', icon: FiDollarSign, path: '/budget' },
  { key: 'casting', name: 'Casting Assistant', desc: 'Find the perfect talent for your roles with AI matching', icon: FiUsers, path: '/cast' },
  { key: 'location', name: 'Location Scouting', desc: 'Discover and manage shooting locations with advanced filtering', icon: FiMapPin, path: '/location' },
];

const StudentToolSelector = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [project, setProject] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) { navigate('/login'); return; }
    (async () => {
      try {
        const statusRes = await fetch(`${baseUrl}/free-tier/status`, { headers: { Authorization: `Bearer ${token}` } });
        if (!statusRes.ok) { navigate(`/toolselector/${projectId}`, { replace: true }); return; }
        const statusData = await statusRes.json();

        // Not a verified, currently-eligible student (expired, or somehow
        // landed here otherwise) - fall back to the existing, untouched flow.
        if (statusData.state !== 'active' && statusData.state !== 'not_started') {
          navigate(`/toolselector/${projectId}`, { replace: true });
          return;
        }
        setStatus(statusData);

        const projRes = await fetch(`${baseUrl}/getProjectById`, {
          headers: { Authorization: `Bearer ${token}`, project_id: String(projectId) },
        });
        if (projRes.ok) setProject(await projRes.json());

        setReady(true);
      } catch (_) {
        navigate(`/toolselector/${projectId}`, { replace: true });
      }
    })();
  }, [projectId, navigate]);

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const usedThisWeek = status?.tools_used_this_week || [];

  return (
    <div className="min-h-screen bg-slate-50 p-6 md:p-10">
      <div className="max-w-5xl mx-auto">
        <button
          onClick={() => navigate('/my-producer-buddy-studentportal')}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-teal-600 mb-6 transition-colors duration-150"
        >
          <FiArrowLeft size={14} /> Back to My Projects
        </button>

        <h1 className="text-2xl font-bold text-slate-800 mb-1">{project?.title || 'Your Project'}</h1>
        <p className="text-sm text-slate-500 mb-6">Choose a tool to work on this project - included free with your student plan.</p>

        <div className="rounded-2xl border border-teal-200 bg-teal-50 p-4 mb-8 flex flex-wrap gap-6 text-sm">
          <div>
            <p className="text-teal-600 font-medium text-xs uppercase tracking-wide">Days left</p>
            <p className="text-slate-800 font-bold">{status.days_left ?? '-'}</p>
          </div>
          <div>
            <p className="text-teal-600 font-medium text-xs uppercase tracking-wide">Sessions this week</p>
            <p className="text-slate-800 font-bold">{status.sessions_used ?? 0} of {status.sessions_limit ?? '-'}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {TOOLS.map((tool) => {
            const Icon = tool.icon;
            const usedFlag = usedThisWeek.includes(tool.key);
            return (
              <button
                key={tool.key}
                onClick={() => navigate(`${tool.path}/${projectId}`)}
                className={`text-left rounded-2xl border-2 p-5 transition-all duration-200 ${
                  usedFlag
                    ? 'border-amber-200 bg-amber-50 hover:border-amber-300'
                    : 'border-slate-200 bg-white hover:border-teal-400 hover:shadow-md'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                    usedFlag ? 'bg-amber-100 text-amber-600' : 'bg-gradient-to-br from-teal-500 to-cyan-500 text-white'
                  }`}>
                    <Icon size={18} />
                  </div>
                  {usedFlag && (
                    <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 px-2 py-1 rounded-full">
                      <FiEye size={10} /> View-only this week
                    </span>
                  )}
                </div>
                <h3 className="font-semibold text-slate-800 mb-1">{tool.name}</h3>
                <p className="text-xs text-slate-500">{tool.desc}</p>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StudentToolSelector;
